import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/api";
import { Video, Upload, FileVideo, ArrowRight } from "lucide-react";

export default function VideoAnalysis() {
  const [coaches, setCoaches] = useState([]);
  useEffect(() => { api.get("/coaches").then((r) => setCoaches(r.data.filter((c) => c.offers_video_analysis))).catch(() => {}); }, []);

  return (
    <div data-testid="video-analysis-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-7">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#ff3b30]">Remote service</span>
          <h1 className="display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter font-bold mt-3 leading-[0.92]">
            Video analysis<br /><span className="text-[#00e5ff]">from anywhere</span>
          </h1>
          <p className="text-base text-zinc-300 leading-relaxed mt-7 max-w-xl">
            No elite goalie coach within driving distance? Send a game or practice film link and get a
            written, timestamped breakdown of depth, tracking, recovery and rebound control.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 border-t border-l border-white/10">
            {[
              { icon: Upload, t: "1. Send film", d: "Paste a YouTube, Drive or Hudl link at checkout." },
              { icon: FileVideo, t: "2. Coach reviews", d: "Timestamped notes on technique and reads." },
              { icon: Video, t: "3. Get your plan", d: "Drills to fix the top three leaks in your game." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="border-r border-b border-white/10 p-6">
                <Icon className="w-5 h-5 text-[#00e5ff] mb-4" />
                <p className="display uppercase font-bold">{t}</p>
                <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 bg-[#121212] border border-white/10 p-8">
          <h2 className="display text-2xl uppercase tracking-tight font-bold">Packages</h2>
          <div className="mt-6 space-y-4">
            {[
              { t: "Standard breakdown", p: 49, f: ["One game or practice film", "Timestamped written notes", "48h turnaround"] },
              { t: "Pro breakdown", p: 99, f: ["Up to three films", "Frame-by-frame positional review", "Custom 4-week drill plan", "24h turnaround"] },
            ].map((pkg) => (
              <div key={pkg.t} className="border border-white/10 p-5 hover:border-[#00e5ff]/50 transition-colors duration-200">
                <div className="flex items-baseline justify-between">
                  <p className="display text-xl uppercase font-bold">{pkg.t}</p>
                  <p className="display text-3xl font-bold text-[#00e5ff]">{money(pkg.p)}</p>
                </div>
                <ul className="mt-3 space-y-1.5">
                  {pkg.f.map((x) => <li key={x} className="text-xs text-zinc-400">— {x}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-6">Pick a coach below, choose a video package on their profile and pay by card.</p>
        </div>
      </div>

      <h2 className="display text-2xl sm:text-3xl uppercase tracking-tight font-bold mt-20 mb-8">Coaches offering analysis</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {coaches.map((c) => (
          <Link key={c.id} to={`/coaches/${c.id}`} data-testid={`video-coach-${c.id}`}
            className="group border border-white/10 bg-[#121212] p-5 hover:border-[#00e5ff]/50 hover:-translate-y-1 transition-transform duration-200">
            <p className="display text-xl uppercase font-bold leading-none">{c.name}</p>
            <p className="text-[11px] text-zinc-500 mt-2">{c.city} · {c.experience_years} yrs · ★ {c.rating}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#00e5ff]">
              Order analysis <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-200" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
